import { inject, Injectable } from '@angular/core';
import { TradingApiService } from '../../core/services/api/trading-api.service';
import { BaseRequest } from '../../shared/models/base-request.model';
import { HttpContext } from '@angular/common/http';
import {
  LOADER_SECTION,
  SHOW_LOADER,
  SKIP_ERROR_HANDLER,
} from '../../core/auth-context';
import {
  mapToGetCustomerAccountsRequestDto,
  mapToGetSalesMarketplaceItemsRequestDto,
  mapToGetSalesOrdersRequestDto,
  mapToGetShoppingCartsRequestDto,
  mapToResolveAccountShippingServicesRequestDto,
} from './mappers/mappers';
import { map, switchMap, throwError, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TradingDomainService {
  private tradingApiService = inject(TradingApiService);

  private buildContext(request: BaseRequest): HttpContext {
    return new HttpContext()
      .set(SHOW_LOADER, request.showLoader ?? false)
      .set(LOADER_SECTION, request.loaderSection ?? null)
      .set(SKIP_ERROR_HANDLER, request.skipErrorHandler ?? false);
  }

  addShopCartItem(request: BaseRequest) {
    const dto = request.params['shopCart'];
    const context = this.buildContext(request);

    return this.tradingApiService.saveShoppingCarts(dto, context);
  }

  checkoutShopCart(request: BaseRequest) {
    const dto = {
      shoppingCartIds: [request.params['id']],
    };
    const context = this.buildContext(request);

    return this.tradingApiService.checkOutShoppingCarts(dto, context).pipe(
      switchMap((res: any) => {

        if (res?.error) {
          return throwError(() => res?.error);
        }
        return of(res?.transaction || null);
      }),
    );
  }

  deleteShopCart(request: BaseRequest) {
    const dto = {
      shoppingCartIds: request.params['shoppingCartIds'],
    };
    const context = this.buildContext(request);

    return this.tradingApiService.deleteShoppingCarts(dto, context);
  }

  deleteShopCartItem(request: BaseRequest) {
    const dto = {
      shoppingCartId: request.params['shopCartId'],
      shoppingCartItemIds: [request.params['shopCartItemId']],
    };
    const context = this.buildContext(request);

    return this.tradingApiService.deleteShoppingCartItems(dto, context);
  }

  getCustomerAccounts(request: BaseRequest) {
    const dto = mapToGetCustomerAccountsRequestDto(request.params);
    const context = this.buildContext(request);

    return this.tradingApiService.getCustomerAccounts(dto, context);
  }

  getListings() {
    const dto = {
      orderBy: 'name',
      paging: {
        pageNumber: 1,
        rows: 50,
      },
    };

    const context = new HttpContext().set(SHOW_LOADER, false);

    return this.tradingApiService.getListings(dto, context);
  }

  getLocation(request: BaseRequest) {
    const dto = {
      id: request.params['id'],
    };
    const context = this.buildContext(request);

    return this.tradingApiService
      .getLocations(dto, context)
      .pipe(map((res: any) => res?.locations?.[0] || null));
  }

  getOrders(request: BaseRequest) {
    const dto = mapToGetSalesOrdersRequestDto(request.params);
    const context = this.buildContext(request);

    return this.tradingApiService.getSalesOrders(dto, context);
  }

  getShopCart(request: BaseRequest) {
    const dto = mapToGetShoppingCartsRequestDto(request.params);
    const context = this.buildContext(request);

    return this.tradingApiService
      .getShoppingCarts(dto, context)
      .pipe(map((res: any) => res?.data?.shoppingCarts?.[0] || null));
  }

  getShopCarts(request: BaseRequest) {
    const dto = mapToGetShoppingCartsRequestDto(request.params);
    const context = this.buildContext(request);

    return this.tradingApiService.getShoppingCarts(dto, context);
  }

  getShopItems(request: BaseRequest) {
    const dto = mapToGetSalesMarketplaceItemsRequestDto(request.params);
    const context = this.buildContext(request);

    return this.tradingApiService.getSalesMarketplaceItems(dto, context);
  }

  resolveAccountShippingServices(request: BaseRequest) {
    const dto = mapToResolveAccountShippingServicesRequestDto(request.params);
    const context = this.buildContext(request);

    return this.tradingApiService.resolveAccountShippingServices(dto, context);
  }
}
